import { useCallback, useMemo } from 'react';
import { DevSettings, Platform, View } from 'react-native';
import { Text } from '@/components/Text';
import { useSentenceSetContext } from '@/contexts/SentenceSetContext/useSentencesetContext';
import {
  BASIC_CHECK_BOX,
  BASIC_NOMEN_CHECK_BOX,
  BASIC_PRONOMEN_CHECK_BOX,
  CheckboxType,
  RadioItemType,
  SATZE_RADIO,
  SATZE_SUBJECT_RADIO,
  SentenceType,
  SubjectType,
} from './config';
import RadioButton from '@/components/ui/therapist/RadioButton';
import Checkbox from '@/components/ui/therapist/Checkbox';

export function Filter() {
  const {
    sentenceType,
    handleArrayState,
    sentenceSubTypes,
    subjectType,
    handleSentenceTypeState,
    handleSubjectTypeState,
  } = useSentenceSetContext();

  const handleSentenceTypeChange = useCallback((value: SentenceType) => {
    return () => {
      const ALL_VALUES =
        value === 'OV' || value === 'VO'
          ? BASIC_CHECK_BOX[value].map((p) => p.value)
          : subjectType == SubjectType.PRONOMEN
            ? BASIC_PRONOMEN_CHECK_BOX[value].map((p) => p.value)
            : BASIC_NOMEN_CHECK_BOX[value].map((p) => p.value);
      handleSentenceTypeState(value, ALL_VALUES);
    };
  }, []);

  const handleSubjectTypeChange = useCallback(
    (value: SubjectType, sentenceType: SentenceType) => {
      return () => {
        handleSubjectTypeState(
          value,
          value == SubjectType.PRONOMEN
            ? BASIC_PRONOMEN_CHECK_BOX[sentenceType].map((p) => p.value)
            : BASIC_NOMEN_CHECK_BOX[sentenceType].map((p) => p.value),
        );
      };
    },
    [],
  );

  const handleBasicChange = (value: string, isRemove: boolean) => {
    handleArrayState(value, isRemove);
  };

  useCallback(() => {
    if (Platform.OS !== 'web') {
      DevSettings.reload();
    } else {
      window.location.reload();
    }
  }, []);

  //check if we can remove this as we udpate the subtype
  const CHECKBOXES = useMemo(() => {
    return sentenceType == 'OV' || sentenceType === 'VO'
      ? BASIC_CHECK_BOX[sentenceType]
      : subjectType == SubjectType.PRONOMEN
        ? BASIC_PRONOMEN_CHECK_BOX[sentenceType]
        : BASIC_NOMEN_CHECK_BOX[sentenceType];
  }, [sentenceType, subjectType]);

  const ACTIVE_CHECKBOXES = useMemo(() => {
    return CHECKBOXES.map((eachCheckBox: CheckboxType) => {
      const { value, label, checkboxLabel } = eachCheckBox;
      const isChecked = sentenceSubTypes.includes(value);

      return (
        <View
          className="flex flex-1 flex-row items-center pt-2"
          key={eachCheckBox.value}
        >
          <Text className="basis-1/4 text-base font-bold">{label}</Text>
          <View>
            {/* @ts-ignore */}
            <Checkbox
              label={checkboxLabel}
              disabled={false}
              isSelected={isChecked}
              onPress={() => handleBasicChange(value, isChecked)}
            />
          </View>
        </View>
      );
    });
  }, [CHECKBOXES.length, sentenceSubTypes]);

  const SATZE_RADIO_RENDER = useMemo(() => {
    return SATZE_RADIO.map((eachWortart: RadioItemType) => {
      const { id, name, value } = eachWortart ?? {};
      const isSelected = value === sentenceType;
      return (
        <View key={id} className="items-start">
          <RadioButton
            isSelected={isSelected}
            label={name}
            labelStyle={{ fontWeight: 'bold' }}
            onPress={handleSentenceTypeChange(value as SentenceType)}
          />
        </View>
      );
    });
  }, [sentenceType]);

  const SATZE_SUBJECT_RADIO_RENDER = useMemo(() => {
    return SATZE_SUBJECT_RADIO.map((eachWortart: RadioItemType) => {
      const { id, name, value } = eachWortart ?? {};
      const isSelected = value === subjectType;
      return (
        <View key={id} className="items-start">
          <RadioButton
            isSelected={isSelected}
            label={name}
            labelStyle={{ fontWeight: 'bold' }}
            onPress={handleSubjectTypeChange(
              value as SubjectType,
              sentenceType,
            )}
          />
        </View>
      );
    });
  }, [sentenceType, subjectType]);

  return (
    <View>
      <View className="space-y-4 p-4 bg-white">
        <View className="flex flex-row flex-wrap space-x-2">
          <Text className="basis-1/4 text-base font-bold">Satzart</Text>
          <View className="flex flex-row space-x-12">{SATZE_RADIO_RENDER}</View>
        </View>
        {sentenceType !== 'OV' && sentenceType !== 'VO' && (
          <View className="flex flex-row flex-wrap space-x-2">
            <Text className="basis-1/4 text-base font-bold">Subjekt</Text>
            <View className="flex flex-row space-x-12">
              {SATZE_SUBJECT_RADIO_RENDER}
            </View>
          </View>
        )}
        <View>{ACTIVE_CHECKBOXES}</View>
      </View>
    </View>
  );
}
